<template>
	<div class="c-script">
		<PageHeader title="AI Scripts" :showMenu="false">
			<template v-slot:title> Write ads that work </template>
		</PageHeader>
		<div class="c-script__inner">
			<header>
				<a
					v-for="stage in stages"
					:key="stage"
					:class="[{ active: isStageActive(stage) }]"
				>
					{{ stage }}
				</a>
			</header>
			<transition-group name="list" mode="out-in">
				<Start key="1" v-if="currentStage === 'start'" @next="onNext"></Start>
				<Ad
					key="2"
					v-if="currentStage === 'ad'"
					:form="form"
					@next="onNext"
				></Ad>
				<Brief key="3" v-if="currentStage === 'brief'" @next="onNext"></Brief>
				<Writer
					key="4"
					v-if="currentStage === 'writer'"
					:form="form"
					@next="onNext"
				></Writer>
				<Results
					key="5"
					v-if="currentStage === 'results'"
					:form="form"
					@next="onNext"
				></Results>
			</transition-group>
		</div>
	</div>
</template>

<script>
import PageHeader from '@swipekit/components/utils/page-header';
import Ad from './ad.vue';
import Start from './start';
import Brief from './brief';
import Writer from './writer';
import Results from './results';

import scripts from '@swipekit/lib/scripts';

export default {
	components: {
		PageHeader,
		Start,
		Ad,
		Brief,
		Writer,
		Results,
	},

	data: function () {
		return {
			currentStage: 'start',
			stages: ['start', 'writer', 'ad', 'brief', 'results'],

			form: {
				sourceAdId: null,
				briefId: null,
				type: '',
				form: {},
				brief: {
					platforms: ['facebook', 'instagram'],
					awareness_level: '',
					production_level: '',
					additional_description: '',
					ad_objective: '',
					unique_selling_point: '',
					target_audience: '',
				},
			},

			initial: {
				briefId: null,
				type: '',
				sourceAdId: null,
			},
		};
	},

	watch: {
		'$route.query': {
			handler() {
				this.saveQueryParams();
			},
			immediate: true,
		},
	},

	methods: {
		isStageActive: function (stage) {
			const currentIndex = this.stages.indexOf(this.currentStage);
			const stageIndex = this.stages.indexOf(stage);
			return stageIndex <= currentIndex;
		},
		onNext: function (data = {}) {
			if (data.briefId) {
				this.form.briefId = data.briefId;
			}
			if (data.id) {
				this.form.id = data.id;
			}
			if (data.type) {
				this.form.type = data.type;
			}
			if (data.form) {
				this.form.form = data.form;
			}
			if (data.ad) {
				if (typeof data.ad === 'object') {
					this.form.sourceAdId = data.ad.id;
				} else {
					this.form.sourceAdId = data.ad;
				}
			}
			let index = this.stages.indexOf(this.currentStage);

			let currentStage = this.stages[index + 1];

			if (currentStage === 'ad') {
				const condition = this.isAdRequired();

				if (!condition) {
					index = this.stages.indexOf('ad');
					currentStage = this.stages[index + 1];
				}
			}

			if (currentStage === 'brief') {
				const condition = this.isBriefRequired();

				if (!condition) {
					index = this.stages.indexOf('brief');
					currentStage = this.stages[index + 1];
				}
			}

			this.currentStage = currentStage;
		},
		isAdRequired: function () {
			let type = this.form.type;

			if (!type) {
				return;
			}

			let required = null;

			for (let i = 0; i < scripts.length; i++) {
				const script = scripts[i];
				if (script.value === type) {
					required = script.required;
					break;
				}
			}

			if (!required) {
				return;
			}

			let condition = false;
			for (let i = 0; i < required.length; i++) {
				let req = required[i];
				if (req.includes('ad')) {
					condition = true;
					break;
				}
			}
			return condition;
		},
		isBriefRequired: function () {
			let type = this.form.type;

			if (!type) {
				return;
			}

			let required = null;

			for (let i = 0; i < scripts.length; i++) {
				const script = scripts[i];
				if (script.value === type) {
					required = script.required;
					break;
				}
			}

			if (!required) {
				return;
			}

			let condition = false;
			for (let i = 0; i < required.length; i++) {
				let req = required[i];
				if (req.includes('brief')) {
					condition = true;
					break;
				}
			}
			return condition;
		},
		saveQueryParams() {
			const qs = this.$route.query;
			if (qs.type) {
				this.form.type = qs.type.toUpperCase();
			}
			if (qs.adId) {
				this.form.sourceAdId = qs.adId;
			}

			if (qs.type && qs.adId && this.currentStage === 'start') {
				this.currentStage = 'writer';
			}
		},
	},
};
</script>

<style lang="scss">
.c-script {
	--page-width: 600px;

	&__inner {
		position: relative;
		z-index: 1;

		> header {
			display: flex;
			justify-content: center;
			align-items: center;
			padding: 1rem var(--spacer-sm);

			a {
				display: inline-block;
				position: relative;
				margin-left: 1rem;
				background-color: var(--color-bg-3);
				padding: 0.25rem 0.75rem;
				font-size: var(--font-size-sm);
				font-weight: 500;
				color: hsl(var(--p), 10%, 50%);
				border-radius: 99px;
				text-transform: capitalize;
				user-select: none;

				&:after {
					content: '';
					position: absolute;
					top: calc(50% - 1px);
					left: -16px;
					width: 16px;
					height: 2px;
					background-color: var(--color-bg-4);
				}

				&:first-child {
					margin-left: 0;

					&:after {
						display: none;
					}
				}

				&.active {
					background-color: var(--color-bg-98);
					color: var(--color-button-font);

					&:after {
						background-color: var(--color-bg-98);
					}
				}
			}
		}
	}

	.list-move, /* apply transition to moving elements */
.list-enter-active,
.list-leave-active {
		transition: all 0.5s ease;
	}

	.list-enter-from,
	.list-leave-to {
		opacity: 0;
		transform: translateX(30px);
	}

	/* ensure leaving items are taken out of layout flow so that moving
   animations can be calculated correctly. */
	.list-leave-active {
		position: absolute;
	}

	.text-center {
		text-align: center;
	}

	.c-input-button {
		.btn {
			width: 100%;
			border-radius: var(--border-radius);
		}
	}
}
</style>
